/**
 * Print one element, by giving it a document of its own.
 *
 * The previous approach printed the page itself: hide everything with
 * `visibility`, pull the invoice to the top with `position: absolute`. It
 * worked, and then it did not — an absolutely positioned element is out of
 * flow, and browsers will not break one across pages. An invoice a few
 * millimetres too tall did not spill onto the second sheet, it moved its whole
 * footer there and left a hand's width of blank paper behind. Shrinking the
 * footer would have bought a line or two and broken again on a booking with
 * more charges on it.
 *
 * So the invoice gets an empty document instead. Nothing else is in it, so
 * nothing else contributes height, nothing has to be hidden, and the content
 * lays out in ordinary flow — which is the only way a browser paginates
 * sensibly. One page when it fits on one page.
 *
 * An iframe rather than `window.open`, because a popup blocker turning up
 * between the button and the paper is a support call nobody can diagnose.
 */

/** Long enough for the stylesheet and the stamp to load, short enough not to hang. */
const ASSET_TIMEOUT_MS = 3000;

export function printElement(elementId: string): boolean {
  const original = document.getElementById(elementId);
  if (original === null) return false;

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('title', 'Print');
  // Off-screen rather than `display: none`: a frame with no box does not
  // always lay its contents out, and an unlaid-out document prints blank.
  frame.style.cssText =
    'position:fixed;right:0;bottom:0;width:1px;height:1px;opacity:0;border:0;';
  document.body.appendChild(frame);

  const inner = frame.contentDocument;
  if (inner === null) {
    frame.remove();
    return false;
  }

  // The app's own stylesheets, so the document looks like it does on screen.
  // The hiding rules in them are scoped to `html.app-shell`, which this
  // document deliberately does not have.
  const styles = [...document.querySelectorAll('link[rel="stylesheet"], style')]
    .map((node) => node.outerHTML)
    .join('');

  inner.open();
  inner.write(
    `<!doctype html><html lang="en"><head><meta charset="utf-8">` +
      // Every URL in here is root-relative — the stylesheet, the stamp. A
      // blank document inherits its base from whoever created it in every
      // browser that matters, but saying so costs one tag and removes the
      // question, and a stamp that silently fails to load is exactly the sort
      // of thing nobody notices until a customer has the paper.
      `<base href="${document.baseURI}">${styles}` +
      `<style>` +
      `@page { margin: 14mm; }` +
      `html, body { margin: 0; padding: 0; background: #fff; }` +
      // The border and rounding are screen furniture; on paper the sheet is
      // the border.
      `#${elementId} { border: 0 !important; padding: 0 !important; }` +
      `</style></head><body>${original.outerHTML}</body></html>`,
  );
  inner.close();

  let printed = false;
  const send = (): void => {
    if (printed) return;
    printed = true;
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    // After the dialog closes. Removing it sooner cancels the print in Safari.
    window.setTimeout(() => frame.remove(), 1000);
  };

  // Whichever comes first: everything loaded, or long enough that waiting is
  // worse than printing without the stamp.
  frame.addEventListener('load', send);
  window.setTimeout(send, ASSET_TIMEOUT_MS);
  return true;
}
